import { useEffect, useState } from 'react';
import './styles/admin.scss';
import { resolveFileUrl } from '../../Constants';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import VyukaTabs from '../../components/admin/VyukaTabs';
import { useConfirm } from '../../components/admin/useConfirm';
import { downloadJson } from '../../components/admin/downloadJson';
import { SUBJECTS, SUBJECT_LABEL, QTYPES, QTYPE_LABEL, QSTATUS, QSTATUS_LABEL, NONE_OPTION_ID } from './adminConstants';

type Opt = { id?: string; text: string; isCorrect: boolean; order: number };
type Q = { id: string; text: string; topic: string; difficulty: number; imageUrl?: string | null; explanation: string; subject: number; type: number; status: number; rejectionNote?: string | null; options: Opt[] };
type Count = { status: number; type: number; subject: number; count: number };

const NONE_TYPE = 2;

export default function AdminQuestionReview() {
    const [status, setStatus] = useState(0);             // default: Neověřeno
    const [subject, setSubject] = useState<number | null>(null);
    const [type, setType] = useState<number | null>(null);
    const [list, setList] = useState<Q[]>([]);
    const [idx, setIdx] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [counts, setCounts] = useState<Count[]>([]);
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [note, setNote] = useState("");
    const api = new ApiHelper();
    const { ask, confirmModal } = useConfirm();

    useEffect(() => { loadCounts(); }, []);
    useEffect(() => { setLoaded(false); setIdx(0); setRejecting(false); load(); }, [status, subject, type]);

    async function load() {
        try {
            const query: any = { status };
            if (subject !== null) query.subject = subject;
            if (type !== null) query.type = type;
            setList(await (await api.adminContentApi.review(query)).json());
        } catch (x: any) { api.adminError(x); } finally { setLoaded(true); }
    }
    async function loadCounts() {
        try { setCounts(await (await api.adminContentApi.reviewCounts()).json()); } catch (x: any) { api.adminError(x); }
    }

    const countFor = (st: number) => counts.filter(c => c.status === st).reduce((s, c) => s + c.count, 0);

    // Drop the question we just acted on and keep the cursor on the next one.
    function advance(id: string) {
        setList(prev => {
            const next = prev.filter(q => q.id !== id);
            setIdx(i => Math.min(i, Math.max(0, next.length - 1)));
            return next;
        });
        setRejecting(false);
        setNote("");
        loadCounts();
    }

    async function approve(q: Q) {
        setBusy(true);
        try { await api.adminContentApi.approveQuestion(q.id); api.alert.success("Otázka schválena"); advance(q.id); }
        catch (x: any) { api.adminError(x); } finally { setBusy(false); }
    }
    async function confirmReject(q: Q) {
        setBusy(true);
        try { await api.adminContentApi.rejectQuestion(q.id, { note: note.trim() || undefined }); api.alert.success("Otázka zamítnuta"); advance(q.id); }
        catch (x: any) { api.adminError(x); } finally { setBusy(false); }
    }

    async function exportRejected() {
        try {
            const data = await (await api.adminContentApi.rejectedExport()).json();
            if (!data?.questions?.length) { api.alert.error("Žádné zamítnuté otázky k exportu"); return; }
            downloadJson("zamitnute-otazky.json", data);
        } catch (x: any) { api.adminError(x); }
    }
    function deleteRejected() {
        ask(<>Smazat <b>všechny</b> zamítnuté otázky? Tuto akci nelze vrátit – nejdřív je vyexportuj.</>, async () => {
            try { await api.adminContentApi.deleteRejected(); api.alert.success("Zamítnuté otázky smazány"); await load(); await loadCounts(); }
            catch (x: any) { api.adminError(x); }
        }, { title: "Smazat zamítnuté?", confirmLabel: "Smazat vše" });
    }

    const q = list[idx];
    // The "none" question's correct answer is the synthetic option appended at test time.
    const rows: Opt[] = q && q.type === NONE_TYPE
        ? [...q.options, { id: NONE_OPTION_ID, text: "Žádná z předchozích odpovědí není správná", isCorrect: true, order: 99 }]
        : (q?.options ?? []);

    return (
        <div>
            <div className="adminHead">
                <div><h1>Výuka</h1><p>Kontrola otázek – schvaluj nebo zamítej naimportované otázky</p></div>
                {status === 2 && (
                    <div className="aActions">
                        <button className="aBtn ghost" onClick={exportRejected}>Exportovat zamítnuté</button>
                        <button className="aBtn danger" onClick={deleteRejected}>Smazat zamítnuté</button>
                    </div>
                )}
            </div>

            <VyukaTabs />

            <div className="reviewFilters">
                <div className="pills">
                    {QSTATUS.map(s => <span key={s.v} className={"pill" + (status === s.v ? " on" : "")} onClick={() => setStatus(s.v)}>{s.l} ({countFor(s.v)})</span>)}
                </div>
                <div className="reviewFilterRow">
                    <select value={subject ?? ""} onChange={e => setSubject(e.target.value === "" ? null : Number(e.target.value))}>
                        <option value="">Všechny předměty</option>
                        {SUBJECTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                    <select value={type ?? ""} onChange={e => setType(e.target.value === "" ? null : Number(e.target.value))}>
                        <option value="">Všechny typy</option>
                        {QTYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                </div>
            </div>

            {!loaded && <div className="aCard"><p className="muted">Načítám…</p></div>}

            {loaded && list.length === 0 && (
                <div className="aCard"><p className="muted">V kategorii „{QSTATUS_LABEL[status]}“ nejsou žádné otázky.</p></div>
            )}

            {loaded && q && (
                <div className="aCard reviewCard">
                    <div className="reviewTop">
                        <span className="muted">{idx + 1} / {list.length}</span>
                        <span className="reviewMeta">
                            <span className="tag">{SUBJECT_LABEL[q.subject]}</span>
                            <span className="tag">{QTYPE_LABEL[q.type]}</span>
                            {q.topic && <span className="tag">{q.topic}</span>}
                            <span className="tag">obtížnost {q.difficulty}</span>
                        </span>
                    </div>

                    <p className="reviewText">{q.text || <span className="muted">(bez znění)</span>}</p>
                    {q.imageUrl && <img className="reviewImg" src={resolveFileUrl(q.imageUrl)} alt="" />}

                    <div className="reviewOptions">
                        {rows.map((o, i) => (
                            <div key={o.id ?? i} className={"reviewOpt" + (o.isCorrect ? " correct" : "")}>
                                <span className="mark">{o.isCorrect ? "✓" : ""}</span>
                                <span>{o.text}</span>
                            </div>
                        ))}
                    </div>

                    {q.explanation && <div className="reviewExpl"><b>Vysvětlení:</b> {q.explanation}</div>}
                    {q.status === 2 && q.rejectionNote && <div className="reviewExpl no"><b>Důvod zamítnutí:</b> {q.rejectionNote}</div>}

                    {rejecting ? (
                        <div className="reviewReject">
                            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Důvod zamítnutí (volitelné)" />
                            <div className="aActions">
                                <button className="aBtn ghost" onClick={() => { setRejecting(false); setNote(""); }}>Zrušit</button>
                                <button className="aBtn danger" onClick={() => confirmReject(q)} disabled={busy}>Potvrdit zamítnutí</button>
                            </div>
                        </div>
                    ) : (
                        <div className="reviewActions">
                            <button className="aBtn ghost" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}>← Předchozí</button>
                            <div className="aActions">
                                {q.status !== 2 && <button className="aBtn danger" onClick={() => setRejecting(true)} disabled={busy}>Zamítnout</button>}
                                {q.status !== 1 && <button className="aBtn" onClick={() => approve(q)} disabled={busy}>Schválit</button>}
                            </div>
                            <button className="aBtn ghost" onClick={() => setIdx(i => Math.min(list.length - 1, i + 1))} disabled={idx >= list.length - 1}>Další →</button>
                        </div>
                    )}
                </div>
            )}

            {confirmModal}
        </div>
    );
}
